import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Typography,
  Divider,
  Alert,
  Autocomplete,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { rulesService } from '../../../services/rulesService'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { events, examples, placeholder } from './data'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { useConfirmAction } from '../../../hooks/useConfirmAction'

// --- VALIDATE API ---
const validateRuleAPI = async (user, dsl, processTemplateId, activityTemplateId) => {
  const res = await rulesService.validateRule(user, {
    dsl,
    processTemplateId,
    activityTemplateId,
  })

  return res
}

// --- SAVE API ---
const saveRuleAPI = async (user, payload) => {
  const res = await rulesService.createRule(user, payload)
  return res
}

const RuleEditorDialog = ({ open, onClose, processTemplateId, activityTemplateId, processId }) => {
  const { user } = useAuthContext()
  const navigate = useNavigate()
  const { confirm, ConfirmDialog } = useConfirmAction()

  const [dsl, setDsl] = useState('')
  const [parsedJson, setParsedJson] = useState(null)

  const [isValidating, setIsValidating] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const [error, setError] = useState(null)
  const [isValid, setIsValid] = useState(false)
  const [rules, setRules] = useState([])

  const [selectedRuleId, setSelectedRuleId] = useState(null)
  const [isEditMode, setIsEditMode] = useState(false)

  const [ruleName, setRuleName] = useState('')
  const [trigger, setTrigger] = useState(events[0] || '')
  const [expiryDateTime, setExpiryDateTime] = useState('')

  const [rightTab, setRightTab] = useState(0)

  // later replace from api
  const [variables, setVariables] = useState([])
  const [routes, setRoutes] = useState([])

  /* const fetchRules = async () => {
    const rules = await rulesService.getRules(user, processTemplateId, activityTemplateId)
    setRules(rules)
  } */

  // Only change required logic. Keep UI same.

  // -------------------- UPDATE fetchRules --------------------
  const fetchRules = async () => {
    const rules = await rulesService.getRules(user, processTemplateId, activityTemplateId)
    setRules(rules)

    // restrict one rule per user + process + activity
    const existingRule = rules.find((rule) => Number(rule.created_by) === Number(user?.id))

    if (existingRule) {
      handleEdit(existingRule) // auto switch to edit mode
    } else {
      // fresh create mode
      setIsEditMode(false)
      setSelectedRuleId(null)
    }
  }

  const getProcessDataVariableKeys = async () => {
    const { keys, routes } = await rulesService.getProcessDataVariableKeys(user, activityTemplateId)
    const variables = keys.map((it) => it.key)
    const routeKeys = routes.map((it) => it.route_template_name)
    setVariables(variables)
    setRoutes(routeKeys)
  }

  useEffect(() => {
    if (open) {
      fetchRules()
      getProcessDataVariableKeys()
    }
  }, [open, processTemplateId, activityTemplateId])

  useEffect(() => {
    if (open) {
      fetchRules()
      getProcessDataVariableKeys()
    } else {
      // reset only when closing
      setDsl('')
      setParsedJson(null)
      setError(null)
      setIsValid(false)
      setRuleName('')
      setTrigger(events[0])
      setExpiryDateTime('')
      setRightTab(0)
      setIsEditMode(false)
      setSelectedRuleId(null)
    }
  }, [open, processTemplateId, activityTemplateId])

  useEffect(() => {
    setTimeout(() => setIsValid(false), 5000)
  }, [])

  const handleValidate = async () => {
    try {
      setIsValidating(true)
      setError(null)

      const res = await validateRuleAPI(user, dsl, processTemplateId, activityTemplateId)

      if (!res?.ast) {
        throw new Error('Invalid response from server')
      }

      setParsedJson(res.ast)
      setIsValid(true)

      setTimeout(() => setIsValid(false), 5000)
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Validation failed')
      setIsValid(false)
      setParsedJson(null)
    } finally {
      setIsValidating(false)
    }
  }

  const handleEdit = (rule) => {
    setDsl(rule.dsl)
    setRuleName(rule.name)
    setTrigger(rule.triggers?.[0] || events[0])
    setSelectedRuleId(rule.id)
    setIsEditMode(true)
    setIsValid(true)
    setExpiryDateTime(rule.valid_till ? new Date(rule.valid_till).toISOString().slice(0, 16) : '')
  }

  const handleDelete = async (ruleId) => {
    try {
      await rulesService.deleteRule(user, ruleId)

      setDsl('')
      setRuleName('')
      setTrigger(events[0])
      setExpiryDateTime('')
      setParsedJson(null)
      setIsValid(false)
      setIsEditMode(false)
      setSelectedRuleId(null)

      await fetchRules()
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Delete failed')
    }
  }

  const handleRunRule = async (ruleId, processId) => {
    confirm({
      actionFn: async () => {
        try {
          await rulesService.triggerRule(user, ruleId, processId)
          setTimeout(() => navigate(-1), 2500) // after successful run, go back to task pages
        } catch (err) {
          setError(err?.response?.data?.error || err.message || 'Execution failed')
        }
      },
      heading: 'Confirm Execution',
      message:
        'Please save your changes before running the rule, as it will be executed against the saved version of the rule.',
      confirmText: 'Run',
    })
  }

  const handleSave = async () => {
    try {
      setIsSaving(true)
      setError(null)

      const payload = {
        name: ruleName || 'Untitled Rule',
        dsl,
        triggers: trigger ? [trigger] : [],
        processTemplateId,
        activityTemplateId,
        priority: 1,
        stopOnMatch: true,
        createdBy: user?.id,
        validTill: expiryDateTime ? new Date(expiryDateTime).toISOString() : null,
      }

      if (isEditMode && selectedRuleId) {
        await rulesService.updateRule(user, selectedRuleId, payload)
      } else {
        await saveRuleAPI(user, payload)
      }

      await fetchRules()
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Save failed')
    } finally {
      setIsSaving(false)
    }
  }

  const copyVariable = async (value) => {
    await navigator.clipboard.writeText(value)
  }

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="lg" fullWidth>
      <DialogTitle>Rule Editor {isEditMode ? '(Edit Mode)' : ''}</DialogTitle>

      <DialogContent>
        <Box display="flex" gap={2} mb={2}>
          <TextField
            style={{ marginTop: 8 }}
            size="small"
            label="Rule Name"
            fullWidth
            value={ruleName}
            onChange={(e) => setRuleName(e.target.value)}
          />

          <Autocomplete
            sx={{ minWidth: 240 }}
            size="small"
            options={events}
            value={trigger || null}
            onChange={(e, newValue) => setTrigger(newValue)}
            renderInput={(params) => (
              <TextField {...params} label="Trigger" style={{ marginTop: 8 }} fullWidth />
            )}
          />

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              label={expiryDateTime ? 'Expiry DateTime' : 'No Expiry'}
              value={expiryDateTime ? dayjs(expiryDateTime) : null}
              onChange={(newValue) => setExpiryDateTime(newValue ? newValue.toISOString() : '')}
              format="DD MMM YYYY, hh:mm A"
              slotProps={{
                textField: {
                  size: 'small',
                  placeholder: 'No Expiry',
                  sx: { minWidth: 220, mt: 1 },
                },
                actionBar: {
                  actions: ['clear', 'accept'],
                },
              }}
            />
          </LocalizationProvider>
        </Box>

        <Box display="flex" gap={2} height="400px">
          {/* LEFT */}
          <Box flex={1} display="flex" flexDirection="column">
            <Typography fontWeight={600} mb={1}>
              DSL Editor
            </Typography>

            <TextField
              multiline
              fullWidth
              minRows={18}
              value={dsl}
              onChange={(e) => {
                setDsl(e.target.value)
                setIsValid(false)
              }}
              placeholder={placeholder}
              spellCheck={false}
              inputProps={{
                spellCheck: false,
                autoCorrect: 'off',
                autoCapitalize: 'off',
                autoComplete: 'off',
                'data-gramm': 'false',
              }}
              sx={{
                '& textarea': {
                  fontFamily: 'monospace',
                },
              }}
            />
          </Box>

          <Divider orientation="vertical" flexItem />

          {/* RIGHT */}
          <Box flex={1} overflow="auto">
            <Tabs value={rightTab} onChange={(e, val) => setRightTab(val)} sx={{ mb: 1 }}>
              <Tab label="Examples" />
              <Tab label="Variables" />
              <Tab label="Votes" />
            </Tabs>

            {/* EXAMPLES TAB */}
            {rightTab === 0 && (
              <Box
                sx={{
                  background: '#f5f5f5',
                  padding: 2,
                  borderRadius: 2,
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  fontSize: 13,
                }}
              >
                {examples}
              </Box>
            )}

            {/* VARIABLES TAB */}
            {rightTab === 1 && (
              <Box
                sx={{
                  border: '1px solid #eee',
                  borderRadius: 2,
                  maxHeight: 320,
                  overflow: 'auto',
                }}
              >
                {variables.map((item) => (
                  <Box
                    key={item}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    px={2}
                    py={1}
                    borderBottom="1px solid #f0f0f0"
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { background: '#fafafa' },
                    }}
                    onClick={() => copyVariable(item)}
                  >
                    <Typography fontSize={13} fontFamily="monospace">
                      {item}
                    </Typography>

                    <Tooltip title="Copy">
                      <ContentCopyIcon fontSize="small" />
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            )}
            {/* VARIABLES TAB */}
            {rightTab === 2 && (
              <Box
                sx={{
                  border: '1px solid #eee',
                  borderRadius: 2,
                  maxHeight: 320,
                  overflow: 'auto',
                }}
              >
                {routes.map((item) => (
                  <Box
                    key={item}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    px={2}
                    py={1}
                    borderBottom="1px solid #f0f0f0"
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { background: '#fafafa' },
                    }}
                    onClick={() => copyVariable(item)}
                  >
                    <Typography fontSize={13} fontFamily="monospace">
                      {item}
                    </Typography>

                    <Tooltip title="Copy">
                      <ContentCopyIcon fontSize="small" />
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            )}

            {/* AST */}
            {/* {parsedJson && (
              <>
                <Typography fontWeight={600} mt={2}>
                  Parsed AST
                </Typography>

                <Box
                  sx={{
                    background: '#111',
                    color: '#0f0',
                    padding: 2,
                    borderRadius: 2,
                    
                    fontFamily: 'monospace',
                    fontSize: 12,
                    maxHeight: 200,
                    overflow: 'auto',
                  }}
                >
                  {JSON.stringify(parsedJson, null, 2)}
                </Box>
              </>
            )} */}
          </Box>
        </Box>

        {/* <Typography fontWeight={600} mt={2}>
          Existing Rules
        </Typography>

        <Box
          sx={{
            maxHeight: 200,
            overflow: 'auto',
            border: '1px solid #ddd',
            borderRadius: 2,
            mt: 1,
          }}
        >
          {rules.length === 0 && (
            <Typography p={2} fontSize={13}>
              No rules found
            </Typography>
          )}

          {rules.map((rule) => (
            <Box
              key={rule.id}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              p={1}
              borderBottom="1px solid #eee"
            >
              <Box>
                <Typography fontSize={13} fontWeight={500}>
                  {rule.name}
                </Typography>

                <Typography fontSize={11} color="text.secondary">
                  {rule.triggers?.join(', ')}
                </Typography>
              </Box>

              <Box display="flex" gap={1}>
                <Button size="small" onClick={() => handleEdit(rule)}>
                  Edit
                </Button>

                <Button size="small" color="error" onClick={() => handleDelete(rule.id)}>
                  Delete
                </Button>
              </Box>
            </Box>
          ))}
        </Box> */}

        <Box mt={2}>
          {error && <Alert severity="error">{error}</Alert>}
          {isValid && <Alert severity="success">Validation successful</Alert>}
        </Box>
      </DialogContent>

      <DialogActions>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
          padding={2}
        >
          {/* Left Side */}
          <Box>
            {isEditMode && selectedRuleId && (
              <Button color="error" variant="outlined" onClick={() => handleDelete(selectedRuleId)}>
                Delete Rule
              </Button>
            )}
            {isEditMode && selectedRuleId && processId && (
              <Button
                style={{ marginLeft: 8 }}
                color="success"
                variant="outlined"
                onClick={() => handleRunRule(selectedRuleId, processId)}
              >
                Run Rule
              </Button>
            )}
          </Box>

          {/* Right Side */}
          <Box display="flex" gap={1}>
            <Button onClick={() => onClose(false)}>Cancel</Button>

            <Button onClick={handleValidate} variant="outlined" disabled={!dsl || isValidating}>
              {isValidating ? 'Validating...' : 'Validate'}
            </Button>

            <Button onClick={handleSave} variant="contained" disabled={!isValid || isSaving}>
              {isSaving ? 'Saving...' : isEditMode ? 'Update' : 'Save'}
            </Button>
          </Box>
        </Box>
      </DialogActions>
      <ConfirmDialog />
    </Dialog>
  )
}

export default RuleEditorDialog
